<template>
  <div v-if="days.length" class="mb-4" data-testid="shared-days">
    <v-card v-for="day in days" :key="day.id" variant="tonal" color="primary" class="mb-2" data-testid="shared-day-card">
      <v-card-text class="py-3">
        <div class="d-flex align-center flex-wrap">
          <div class="flex-grow-1">
            <div class="font-weight-medium">
              <v-icon size="small" class="mr-1">mdi-link-variant</v-icon>
              Shared day — {{ day.personName ?? 'Person' }}
            </div>
            <div class="text-caption">
              One {{ day.rateType === 'hourly' ? 'hourly' : 'day' }} rate of ${{ day.rate.amount.toLocaleString() }}{{ day.rateType === 'hourly' ? '/hr' : '/day' }};
              overtime counts the combined hours and the cost is split by hours worked.
            </div>
          </div>
          <div v-if="canManage && allDraft(day)" class="d-flex ga-1">
            <v-btn size="small" color="primary" :loading="busy" data-testid="send-shared-offer" @click="$emit('send', day)">
              Send one offer
            </v-btn>
            <v-btn size="small" variant="text" :disabled="busy" data-testid="unlink-shared-day" @click="$emit('unlink', day)">
              Unlink
            </v-btn>
          </div>
        </div>
        <v-table density="compact" class="mt-2 bg-transparent">
          <tbody>
            <tr v-for="m in day.members" :key="m.bookingId">
              <td>
                {{ gigNames[m.gigId] ?? 'Gig' }}
                <span v-if="m.gigId === gigId" class="text-caption">(this gig)</span>
              </td>
              <td>{{ memberData.get(m.bookingId)?.position.role ?? '' }}</td>
              <td>
                <v-chip v-if="memberData.get(m.bookingId)" size="x-small">{{ memberData.get(m.bookingId)!.booking.state }}</v-chip>
              </td>
              <td class="text-right">
                <template v-if="estimate?.has(m.bookingId)">${{ total(m.bookingId).toLocaleString() }}</template>
                <span v-else class="text-medium-emphasis">…</span>
              </td>
            </tr>
            <tr class="font-weight-medium">
              <td colspan="3">Estimated for the whole day</td>
              <td class="text-right">${{ dayTotal(day).toLocaleString() }}</td>
            </tr>
          </tbody>
        </v-table>
        <div v-if="!allDraft(day)" class="text-caption mt-1">
          The offer has gone out, so the link is locked. Declining or cancelling either booking unlinks the day.
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import type { SharedAllocation, SharedDay, SharedMemberData } from '@gigdriver/domain';
import { sharedTotal } from '@gigdriver/domain';

const props = defineProps<{
  days: SharedDay[];
  gigId: string;
  gigNames: Record<string, string>;
  memberData: Map<string, SharedMemberData>;
  estimate?: SharedAllocation;
  canManage: boolean;
  busy: boolean;
}>();
defineEmits<{ (e: 'send', day: SharedDay): void; (e: 'unlink', day: SharedDay): void }>();

const total = (bookingId: string) => (props.estimate ? sharedTotal(props.estimate, bookingId) : 0);
const dayTotal = (day: SharedDay) => day.members.reduce((sum, m) => sum + total(m.bookingId), 0);
// Sending and unlinking are only possible while every member is still a draft.
const allDraft = (day: SharedDay) =>
  day.members.every(m => props.memberData.get(m.bookingId)?.booking.state === 'DRAFT');
</script>
